import { type ParsedUrlQuery } from 'querystring';

import { InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AggregateOffer, ItemList, WithContext } from 'schema-dts';

import { getItineraries } from '../../../core/Api/Front/Resources';
import { logger } from '../../../core/Log/logger';
import { usePagination } from '../../../core/Pagination/Pagination';
import {
    useExperimentVariation,
    useTrackPageViewAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { LogoLoader } from '../../../design-system/components/loader/logo/LogoLoader';
import { PaginationNavigation } from '../../../design-system/components/pagination/PaginationNavigation';
import { boundsFromBoundingBox } from '../../../design-system/components/wemap/utilities/boundsFromBoundingBox';
import { useItinerariesBreadcrumbItems } from '../../../domains/Layout/Breadcrumb/Breadcrumb';
import { useBreakPoint } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { Footer } from '../../../domains/Layout/Footer/Footer';
import { Head } from '../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../domains/Layout/Head/JsonLd';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../domains/Layout/PrimaryNav/PrimaryNav';
import { Facets } from '../../../partials/destination/partials/Facets';
import { AbTripDesignerBanner } from '../../../partials/facet/partials/AbAccessPathfinderTripDesigner/AbTripDesignerBanner';
import { getServerSideProps } from '../../../partials/itinerary-list/getServerSideProps';
import { CreateTrip } from '../../../partials/itinerary-list/partials/CreateTrip';
import { Filters } from '../../../partials/itinerary-list/partials/Filters';
import { Header } from '../../../partials/itinerary-list/partials/Header';
import { ItinerariesGrid } from '../../../partials/itinerary-list/partials/ItinerariesGrid';
import { ItinerariesListTitle } from '../../../partials/itinerary-list/partials/ItinerariesListTitle';
import { MapSection } from '../../../partials/itinerary-list/partials/MapSection';
import { SortValue } from '../../../partials/itinerary-list/partials/Sort';
import { useGenerateItineraryListUrl } from '../../../partials/itinerary-list/shared/hooks/useGenerateItineraryListUrl';
import { getFiltersHash } from '../../../partials/itinerary-list/shared/utilities/getFiltersHash';
import {
    getFiltersQueryParams,
    queryParamsAsApiFilters,
} from '../../../partials/itinerary-list/shared/utilities/queryParamsAsFilters';
import { useCurrentPageFromRouterAndPagination } from '../../../partials/shared/useCurrentPageFromRouterAndPagination';
import { mapItineraryCardTagsToTotalCounts } from '../../../resources/itinerary/domain/operations/getAmplitudeTags';
import { formatPointOfInterestIntoWeMapPinPoint } from '../../../resources/pointOfInterest/domain/operations/formatPlaceForMap';
import { keepUniquePointsOfInterest } from '../../../resources/pointOfInterest/domain/operations/getUniquePointsOfInterest';
import { AgencyListSection } from '../../../shared/components/AgencyListSection/AgencyListSection';
import { useIntersectOnce } from '../../../shared/hooks/useIntersectOnce';
import { useLocale } from '../../../shared/hooks/useLocale';
import { usePrevious } from '../../../shared/hooks/usePrevious';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../shared/providers/currentSite/useCurrentSite';
import { roundedPriceWithCurrencySymbol } from '../../../shared/utilities/priceFormat/priceFormat';
import { FetchItinerariesSort } from '../../api/front/itineraries';

import style from './index.module.css';

export { getServerSideProps };

export type PageItineraryListProps = InferGetServerSidePropsType<typeof getServerSideProps>;

function PageItineraryList(props: PageItineraryListProps) {
    const { locale } = useLocale();
    const { currentSite } = useCurrentSite();
    const router = useRouter();
    const { formatMessage } = useIntl();
    const { generateUrl } = useUrlGenerator();
    const [activeItineraryId, setActiveItineraryId] = useState<number | null>(null);
    const { isXl, isXxl } = useBreakPoint();
    const generateItineraryListUrl = useGenerateItineraryListUrl();
    const agencyListSectionRef = useRef<HTMLDivElement>(null);

    const {
        primaryNav,
        hrefLangs,
        destination,
        facets,
        itineraries,
        destinationItinerariesCount,
        destinationArticle,
        availableFilters,
        agencies,
        lowestItineraryPrice,
        highestItineraryPrice,
    } = props.data;

    const destinationSlug = destination.slug;
    const { itemsPerPage } = itineraries.pagination;
    const filters = getFiltersQueryParams(router.query, availableFilters);
    const [sort, setSort] = useState<SortValue>('pertinence');
    const previousSort = usePrevious(sort, sort);
    const filtersHash = getFiltersHash(filters);
    const previousFiltersHash = usePrevious(filtersHash, filtersHash);

    const pagination = usePagination({
        pagination: itineraries.pagination,
        items: itineraries.items,
        fetcher: paginationFetcher,
    });
    const currentPage = useCurrentPageFromRouterAndPagination(pagination);
    const currentPageItineraries = pagination.currentPageItems();
    const currentPageItinerariesCount = currentPageItineraries.length;

    const hasUserScrolledCloseToAgencyListSection = useIntersectOnce(agencyListSectionRef, {
        root: null,
        rootMargin: '300px',
        threshold: 0,
    });

    const allItineraries = pagination.allItems();
    const allPointsOfInterest = keepUniquePointsOfInterest(
        allItineraries.flatMap((itinerary) => {
            return itinerary.pointsOfInterest;
        }),
    );
    const allPinpoints = allPointsOfInterest.map(formatPointOfInterestIntoWeMapPinPoint);

    const activeItinerary = allItineraries.find((itinerary) => {
        return itinerary.id === activeItineraryId;
    });
    const activeItineraryPointsOfInterest = activeItinerary?.pointsOfInterest ?? [];
    const activeItineraryPinpoints = activeItineraryPointsOfInterest.map(
        formatPointOfInterestIntoWeMapPinPoint,
    );

    const bounds = destination.geolocation
        ? boundsFromBoundingBox(destination.geolocation.boundingBox)
        : undefined;

    const { breadcrumbItems } = useItinerariesBreadcrumbItems({ destination });

    const metaTitle = formatMessage(
        {
            id: 'page.itinerariesList.meta.titleNew',
        },
        {
            destinationName: destination.name,
            itinerariesCount: destinationItinerariesCount,
        },
    );

    const canonicalUrl = generateItineraryListUrl({
        destinationSlug,
        filters,
        absolute: true,
        page: 1,
    });

    const metaDescription = formatMessage(
        {
            id: 'page.itinerariesList.meta.description',
        },
        {
            destinationPreposition: destination.preposition,
            destinationName: destination.name,
        },
    );

    const metaPrev =
        itineraries.pagination.currentPage > itineraries.pagination.firstPage
            ? generateItineraryListUrl({
                  destinationSlug: destination.slug,
                  page: itineraries.pagination.currentPage - 1,
                  filters,
                  absolute: false,
              })
            : undefined;

    const metaNext =
        itineraries.pagination.currentPage < itineraries.pagination.lastPage
            ? generateItineraryListUrl({
                  destinationSlug: destination.slug,
                  page: itineraries.pagination.currentPage + 1,
                  filters,
                  absolute: false,
              })
            : undefined;

    function itemListJson(): WithContext<ItemList> {
        const firstItineraries = itineraries.items.slice(0, 5);

        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: lowestItineraryPrice
                ? formatMessage(
                      {
                          id: 'page.itinerariesList.itineraries.resultsTitleWithLowestPrice',
                      },
                      {
                          itinerariesCount: destinationItinerariesCount,
                          destinationPreposition: destination.preposition,
                          destinationName: destination.name,
                          lowestPrice: roundedPriceWithCurrencySymbol({
                              locale,
                              currency: currentSite.currency,
                              price: lowestItineraryPrice,
                          }),
                      },
                  )
                : formatMessage(
                      {
                          id: 'page.itinerariesList.itineraries.resultsTitle',
                      },
                      {
                          itinerariesCount: destinationItinerariesCount,
                          destinationPreposition: destination.preposition,
                          destinationName: destination.name,
                      },
                  ),
            numberOfItems: destinationItinerariesCount,
            itemListElement: firstItineraries.map((itinerary) => {
                const price = itinerary.startingPrice[currentSite.currency];
                const itineraryUrl = generateUrl('itinerary', {
                    destinationSlug: destination.slug,
                    itineraryId: itinerary.id,
                    itinerarySlug: itinerary.slug,
                });

                return {
                    '@type': 'TouristTrip',
                    name: itinerary.title,
                    description: itinerary.description,
                    offers: {
                        '@type': 'Offer',
                        price,
                        priceCurrency: currentSite.currency,
                        url: itineraryUrl,
                    },
                };
            }),
        };
    }

    function aggregateOfferJsonLd(): WithContext<AggregateOffer> | null {
        if (!lowestItineraryPrice || !highestItineraryPrice) {
            return null;
        }

        const { currency } = currentSite;

        return {
            '@context': 'https://schema.org',
            '@type': 'AggregateOffer',
            priceCurrency: currency,
            lowPrice: lowestItineraryPrice[currency],
            highPrice: highestItineraryPrice[currency],
            offerCount: destinationItinerariesCount,
        };
    }

    const jsonLd = useJsonLd([itemListJson(), aggregateOfferJsonLd()]);

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        const props = {
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
            nb_itineraries_displayed: currentPageItinerariesCount,
            has_alert_message: Boolean(destination.alertMessage),
            nb_tags_list: mapItineraryCardTagsToTotalCounts(allItineraries),
        };

        ampli.viewedPageListItineraries(props);
    });

    async function paginationFetcher({
        itemsPerPage,
        page,
    }: {
        itemsPerPage: number;
        page: number;
    }) {
        return getItineraries({
            locale,
            page,
            perPage: itemsPerPage,
            filters: [
                ...queryParamsAsApiFilters(filters, availableFilters),
                { key: 'destinationId' as const, value: destination.id },
            ],
            sort: formatSortForApi(sort, filters),
        });
    }

    const navigate = useCallback(
        ({ filters, page }: { filters: ParsedUrlQuery; page: number }) => {
            const query = { ...filters };
            if (page > 1) {
                query['p'] = String(page);
            }

            const nextQuery = {
                ...query,
                destinationSlug,
            };
            const nextUrl = {
                pathname: router.pathname,
                query: nextQuery,
            };

            const asQuery = { ...query };
            const asUrl = {
                pathname: router.asPath.split('?')[0],
                query: asQuery,
            };

            const options = {
                shallow: true,
                scroll: true,
            };

            return router.push(nextUrl, asUrl, options);
        },
        [router, destinationSlug],
    );

    const handlePageChange = useCallback(
        async (page: number) => {
            return navigate({ filters, page }).catch((error) => {
                logger.error('Router push failed on page change', { filters, page }, error);
            });
        },
        [navigate, filters],
    );

    const handleFiltersChange = useCallback(
        async (filters: ParsedUrlQuery) => {
            const page = 1;
            return navigate({ filters, page }).catch((error) => {
                logger.error('Router push failed on filters change', { filters, page }, error);
            });
        },
        [navigate],
    );

    const handleRemoveAllFilters = useCallback(async () => {
        return navigate({ filters: {}, page: 1 }).catch((error) => {
            logger.error('Router push failed on remove all filters', {}, error);
        });
    }, [navigate]);

    // @todo: it should not be done that way and probably not here...
    const filterActivityType = filters['activity-type'] ?? [];
    const translatedAvailableFilters = (
        Array.isArray(filterActivityType) ? filterActivityType : [filterActivityType]
    )
        .map(() => {
            const filter = availableFilters.find((filter) => {
                return filter.name === 'activity-type';
            });
            if (!filter) {
                return null;
            }
            const choice = filter.choices.find((choice) => {
                return choice.name === filterActivityType;
            });
            return choice ?? null;
        })
        .filter((filter) => {
            return filter !== null;
        });

    useEffect(
        function resetPaginationOnFiltersChange() {
            if (previousFiltersHash === filtersHash) {
                return;
            }

            pagination.reset({
                currentPage: 1,
                itemsCount: 0,
                lastPage: 1,
                firstPage: 1,
                itemsPerPage,
            });

            void pagination.goToPage(1);
        },
        [filtersHash, previousFiltersHash, pagination, itemsPerPage],
    );

    useEffect(
        function resetPaginationOnSortChange() {
            if (previousSort === sort) {
                return;
            }

            pagination.reset({
                currentPage: 1,
                itemsCount: 0,
                lastPage: 1,
                firstPage: 1,
                itemsPerPage,
            });

            void pagination.goToPage(1);
        },
        [pagination, itemsPerPage, sort, previousSort],
    );

    useEffect(
        function changePageOnPaginationChange() {
            const currentPageFromPaginationState = pagination.currentPage;

            if (
                currentPage === currentPageFromPaginationState ||
                pagination.isPageLoading(currentPage)
            ) {
                return;
            }

            void pagination.goToPage(currentPage).catch((error) => {
                void handlePageChange(currentPageFromPaginationState);

                logger.error(
                    'PaginationNavigation fetch failed',
                    {
                        previousPage: currentPageFromPaginationState,
                        page: currentPage,
                        pagination: {
                            firstPage: pagination.firstPage,
                            previousPage: pagination.previousPage,
                            currentPage: pagination.currentPage,
                            nextPage: pagination.nextPage,
                            lastPage: pagination.lastPage,
                        },
                    },
                    error,
                );
            });
        },
        [currentPage, pagination, handlePageChange],
    );

    const ABPathfinderExperimentTripDesigner = useExperimentVariation(
        'fr-x-pathfinder-destination-list-itinerary-trip-designer',
        {
            destination_slug: destination.slug,
        },
    );
    const isABPathfinderExperimentTripDesigner =
        ABPathfinderExperimentTripDesigner?.value === 'variation';

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={canonicalUrl}
                og={{
                    title: metaTitle,
                    description: metaDescription,
                    image: imageLoaderForId({
                        aspectRatio: 1.91,
                    })({
                        src: destination.coverPicture.id.toString(),
                        width: 1200,
                    }),
                    locale,
                    url: generateUrl('itinerary_list', {
                        destinationSlug: destination.slug,
                    }),
                    type: 'website',
                    site_name: currentSite.organisation.name,
                }}
                prev={metaPrev}
                next={metaNext}
                hrefLangs={hrefLangs}
                jsonLd={jsonLd}
            />

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className="mb-72">
                <div className="mb-40 flex">
                    <div className="col-xl-7 col-12">
                        <Header
                            destination={destination}
                            breadcrumbItems={breadcrumbItems}
                            destinationArticle={destinationArticle}
                        />

                        <div className="mx-16 sm:mx-40">
                            <Filters
                                isLoading={pagination.isAnyPageLoading()}
                                availableFilters={availableFilters}
                                filters={filters}
                                onFilter={handleFiltersChange}
                                onRemoveAllFilters={handleRemoveAllFilters}
                            />
                            <div className="mb-40 mt-24">
                                <ItinerariesListTitle
                                    isLoading={pagination.isPageLoading(currentPage)}
                                    destinationName={destination.name}
                                    destinationPreposition={destination.preposition}
                                    itinerariesCount={pagination.itemsCount}
                                    lowestItineraryPrice={lowestItineraryPrice}
                                    setSort={setSort}
                                />
                            </div>
                            <div className="mb-40">
                                <ItinerariesGrid
                                    isLoading={pagination.isPageLoading(1)}
                                    destination={destination}
                                    itineraries={currentPageItineraries}
                                    selectedFilters={translatedAvailableFilters}
                                    onItineraryHovered={(itinerary) => {
                                        setActiveItineraryId(itinerary.id);
                                    }}
                                />

                                {pagination.isAnyPageLoading() ? (
                                    <div className={style.loaderContainer}>
                                        <LogoLoader />
                                    </div>
                                ) : null}

                                {pagination.lastPage > 1 ? (
                                    <div className={style.paginationContainer}>
                                        <PaginationNavigation
                                            pagination={pagination}
                                            onPageChange={handlePageChange}
                                            generateUrl={(page) => {
                                                return generateItineraryListUrl({
                                                    destinationSlug: destination.slug,
                                                    page,
                                                    filters,
                                                    absolute: false,
                                                });
                                            }}
                                            trackingInformation={{
                                                click: {
                                                    block: 'itineraries',
                                                },
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {(isXl() || isXxl()) && bounds ? (
                        <MapSection
                            bounds={bounds}
                            pinpoints={activeItinerary ? activeItineraryPinpoints : allPinpoints}
                        />
                    ) : null}
                </div>
            </div>
            {agencies.length > 0 ? (
                <div className="bg-beige-100">
                    <div className="layout-with-centered-content py-40 lg:py-56">
                        <section className="main-container">
                            <AgencyListSection
                                forceDisplaySkeleton={!hasUserScrolledCloseToAgencyListSection}
                                destination={destination}
                                agencies={agencies}
                                subTitleElement="div"
                                ref={agencyListSectionRef}
                            />
                        </section>
                    </div>
                </div>
            ) : null}
            <div className="layout-with-centered-content mb-72">
                <div className="main-container mb-96">
                    {facets.length > 0 ? (
                        <section className="main-container py-24 last:pb-0 md:py-32 xl:py-48">
                            <Facets
                                titleElement="div"
                                destination={destination}
                                facets={facets}
                                facetTitleElement="div"
                            />
                        </section>
                    ) : null}
                </div>

                {destination.isAvailableForQuoteRequest ? (
                    <div className="main-container mb-96 flex justify-center">
                        <CreateTrip destination={destination} />
                    </div>
                ) : null}

                {destinationArticle?.advices ? (
                    <div className="main-container">
                        <h2 className="font-l-extrabold mb-16">
                            {formatMessage(
                                { id: 'page.itinerariesList.destination.advices' },
                                {
                                    destinationPreposition: destination.preposition,
                                    destinationName: destination.name,
                                },
                            )}
                        </h2>
                        <HtmlContent htmlContent={destinationArticle.advices} />
                    </div>
                ) : null}
            </div>

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
            {isABPathfinderExperimentTripDesigner ? (
                <AbTripDesignerBanner destination={destination} source="list-itinerary" />
            ) : null}
        </>
    );
}

export default PageItineraryList;

function formatSortForApi(sortOption: SortValue, filters: ParsedUrlQuery): FetchItinerariesSort {
    if (sortOption === 'pricingAsc') {
        return { key: 'price', value: 'ASC' };
    }
    if (sortOption === 'pricingDesc') {
        return { key: 'price', value: 'DESC' };
    }
    if (filters['type-of-profile']?.includes('family')) {
        return { key: 'algorithm', value: 'family-pertinence' };
    }
    return { key: 'algorithm', value: 'pertinence' };
}
